/** Initiative is a Dexterity check. The roster's initiative value is its
 * starting modifier; a rolled token keeps that modifier separately.
 * D&D Basic Rules, Initiative. date_of_change: 2026-11-04
 */
export function initiativeModifierFor(token: any): number {
  const modifier = token?.initiativeModifier ?? token?.initiative;
  if (Number.isInteger(modifier)) return modifier;
  const dexterity = token?.abilities?.dexterity;
  if (Number.isInteger(dexterity)) return Math.floor((dexterity - 10) / 2);
  throw new Error('Initiative needs a Dexterity modifier.');
}

export function buildInitiativeRoll(d20: number, modifier: number, manual: boolean) {
  if (!Number.isInteger(d20) || d20 < 1 || d20 > 20 || !Number.isInteger(modifier)) {
    throw new Error('Initiative requires a d20 face from 1 to 20 and an integer modifier.');
  }
  const formula = `1d20${modifier < 0 ? modifier : `+${modifier}`}`;
  return { d20, modifier, manual, formula, total: d20 + modifier };
}

export function isCurrentInitiativeRoll(roll: any, modifier: number, total: number): boolean {
  return Number.isInteger(roll?.d20) && roll.d20 >= 1 && roll.d20 <= 20
    && roll.modifier === modifier && roll.total === roll.d20 + modifier && roll.total === total
    && typeof roll.manual === 'boolean';
}

// Keep tied totals in the same order on the server and after a drawer remount.
export function compareInitiative(left: { id: string; initiative: number }, right: { id: string; initiative: number }): number {
  return right.initiative - left.initiative || (left.id < right.id ? -1 : Number(left.id > right.id));
}

export function describeInitiativeRoll(name: string, roll: ReturnType<typeof buildInitiativeRoll>): string {
  const modifier = roll.modifier < 0 ? `− ${-roll.modifier}` : `+ ${roll.modifier}`;
  return `${name} rolled initiative: ${roll.formula} → ${roll.d20} ${modifier} = ${roll.total}${roll.manual ? ' (manual d20)' : ''}.`;
}

export function initiativeLogEntry(actor: any) {
  return {
    type: 'INITIATIVE_CHANGED',
    round: 1,
    turn: 0,
    combatantId: actor.id,
    combatantName: actor.name,
    data: { initiativeRoll: actor.initiativeRoll },
    description: describeInitiativeRoll(actor.name, actor.initiativeRoll),
  };
}
